import {useRef, useState} from "react";
import {Alert, Button, Col, message, Modal, Progress, Row, Space} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  PlusCircleFilled,
} from "@ant-design/icons";
import {
  ProductContent,
  useDigitalProductContentList,
  useDigitalProductContentMutations,
} from "../../../../../features/digital-products";
import {FullPageLoader} from "../../../../../components/full-page-loader";
import {OfferingFormAccordionItem} from "../../../../../components/offering-form/accordion/accordion-item";
import {
  StyledProductContentUploadContentListItem,
  StyledProductContentUploadContentListWrapper,
} from "../../../../../components/offering-form/products/content-upload/style";
import {ProductRenameContentModalContent} from "../../../../../components/offering-form/products/content-upload/content-list/rename-content/modal";
import {StyledModalContentWrapper} from "../../../../../components/calls/upcoming-calls/call-card/call-card.style";
import {useCreateProductContext} from "../../context/create-product";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";

const MAX_FILE_SIZE_MB = 100;
const MAX_FILES = 4;
const ALLOWED_EXTENSIONS = ["docx", "pdf", "csv", "xlsx", "xls", "zip"];

function isAllowedFile(file: File): boolean {
  const fileType = file.type.split("/")[0]?.toLowerCase();
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return (
    fileType === "video" || fileType === "audio" || ALLOWED_EXTENSIONS.includes(extension)
  );
}

type PendingUpload = {
  fileName: string;
  percent: number;
};

/**
 * v2 content-upload step for the create-product wizard.
 *
 * By the time this step is reached, the product was already created via
 * {@link useDigitalProductMutations} (basicForm step), so `apiResponseData`
 * always carries a real v2 digital-product id here. Mirrors the read/write
 * pattern used by the edit-product content tab (`EditProductContentPanel`):
 * list + upload + delete go through the v2 digital-products slice; rename
 * stays on the v1 hook (reused modal), which refetches the v2 content query
 * on ok.
 */
export function CreateProductContentUploadStep() {
  const {
    dispatch,
    state: {apiResponseData},
  } = useCreateProductContext();

  const productId = apiResponseData?.product?.id;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [renameTarget, setRenameTarget] = useState<ProductContent | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);

  const contentQuery = useDigitalProductContentList(productId);
  const {uploadContent, isUploadingContent, deleteContent, isDeletingContent} =
    useDigitalProductContentMutations(productId ?? 0);

  const contents = contentQuery.data ?? [];
  const atCap = contents.length >= MAX_FILES;

  function handleFileInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !productId) {
      return;
    }
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      message.error("Max file size is 100MB");
      return;
    }
    if (!isAllowedFile(file)) {
      message.error("File not allowed! Only video, audio and Ebook formats are allowed");
      return;
    }
    setPendingUpload({fileName: file.name, percent: 0});
    uploadContent(
      {
        file,
        options: {
          onProgress: (percent) =>
            setPendingUpload((current) => (current ? {...current, percent} : current)),
        },
      },
      {
        onSuccess: () => message.success("Content uploaded successfully"),
        onError: (error) =>
          message.error(
            error.response?.data?.message || error.message || "Failed to upload content"
          ),
        onSettled: () => {
          setPendingUpload(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        },
      }
    );
  }

  function handleDelete(content: ProductContent) {
    setDeletingId(content.id);
    deleteContent(
      {contentId: content.id},
      {
        onSuccess: () =>
          message.success("The selected content has been deleted successfully!"),
        onError: (error) =>
          message.error(
            error.response?.data?.message || error.message || "Failed to delete content"
          ),
        onSettled: () => setDeletingId(null),
      }
    );
  }

  function handleRenameModalOk() {
    setRenameTarget(null);
    contentQuery.refetch();
  }

  function handleNext() {
    dispatch({
      type: "update-create-product-form-step",
      payload: "variantForm",
    });
  }

  function handleBack() {
    dispatch({
      type: "update-create-product-form-step",
      payload: "coverImagesUpload",
    });
  }

  return (
    <Space direction="vertical" size={16} style={{display: "flex"}}>
      <OfferingFormAccordionItem key="Content upload" index={0} title="Content upload">
        {!productId ? (
          <Alert
            message="Error"
            description="Missing product id. Go back to step 1."
            type="error"
            showIcon
          />
        ) : contentQuery.isLoading ? (
          <FullPageLoader />
        ) : contentQuery.isError ? (
          <Alert
            message="Error"
            description={contentQuery.error?.message}
            type="error"
            showIcon
          />
        ) : (
          <StyledProductContentUploadContentListWrapper>
            <Space size={16} direction="vertical" style={{display: "flex"}}>
              <p className="product-content-list-title">
                You can add multiple files. PDF, Videos, Audio, Docx up to 100MB of size.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileInputChange}
                style={{display: "none"}}
              />
              {contents.length > 0 && (
                <Space size={16} direction="vertical" style={{display: "flex"}}>
                  {contents.map((content) => (
                    <StyledProductContentUploadContentListItem key={content.id}>
                      <div className="product-content-list-item-info">
                        <div className="product-content-list-item-name-wrapper">
                          <FileOutlined className="product-content-list-item-file-icon" />{" "}
                          <p className="product-content-list-item-name">
                            {`${content.fileName} · ${content.humanReadableFileSize}`}
                          </p>
                        </div>
                        <div>
                          <Space size={16}>
                            <Button
                              type="text"
                              shape="circle"
                              icon={<EditOutlined />}
                              title="Edit content"
                              size="small"
                              onClick={() => setRenameTarget(content)}
                            />
                            <Button
                              type="text"
                              shape="circle"
                              icon={<DeleteOutlined />}
                              title="Delete content"
                              size="small"
                              loading={isDeletingContent && deletingId === content.id}
                              onClick={() => handleDelete(content)}
                            />
                          </Space>
                        </div>
                      </div>
                    </StyledProductContentUploadContentListItem>
                  ))}
                </Space>
              )}
              {pendingUpload && (
                <StyledProductContentUploadContentListItem>
                  <div className="product-content-list-item-info">
                    <div
                      className="product-content-list-item-name-wrapper"
                      style={{flexDirection: "column", gap: "0.8rem", flex: 1}}
                    >
                      <div style={{display: "flex", alignItems: "center", gap: "0.8rem"}}>
                        <FileOutlined className="product-content-list-item-file-icon" />
                        <p className="product-content-list-item-name">
                          {pendingUpload.fileName} — Uploading…
                        </p>
                      </div>
                      <Progress
                        percent={pendingUpload.percent}
                        size="small"
                        status="active"
                        strokeColor="#222222"
                      />
                    </div>
                  </div>
                </StyledProductContentUploadContentListItem>
              )}
              {!atCap && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    size="small"
                    icon={<PlusCircleFilled />}
                    disabled={isDeletingContent}
                    loading={isUploadingContent}
                  >
                    Add content
                  </Button>
                </div>
              )}
            </Space>
          </StyledProductContentUploadContentListWrapper>
        )}
      </OfferingFormAccordionItem>

      <Row gutter={[0, 16]}>
        <Col span={24} sm={12}>
          <StyledServiceButtonWrapper>
            <Button
              className="secondary-btn"
              onClick={handleBack}
              block
              size="small"
              type="default"
              disabled={isUploadingContent}
            >
              Back
            </Button>
            <Button
              block
              type="primary"
              htmlType={"button"}
              onClick={handleNext}
              size="small"
              // Content upload is required: block continuing until at least
              // one file is uploaded (contents mirrors the product's v2 content list).
              disabled={contents.length === 0 || isUploadingContent}
            >
              Next
            </Button>
          </StyledServiceButtonWrapper>
        </Col>
      </Row>

      {renameTarget && productId && (
        <Modal
          title="Rename file"
          footer={null}
          visible={!!renameTarget}
          onOk={handleRenameModalOk}
          onCancel={() => setRenameTarget(null)}
          maskClosable={false}
        >
          <StyledModalContentWrapper>
            <ProductRenameContentModalContent
              renameModalDetails={{
                fileName: renameTarget.fileName,
                fileExtension: renameTarget.fileType,
                contentId: renameTarget.id,
                productId,
              }}
              handleRenameContentModalOk={handleRenameModalOk}
              handleRenameContentModalCancel={() => setRenameTarget(null)}
            />
          </StyledModalContentWrapper>
        </Modal>
      )}
    </Space>
  );
}
